import type { ChangeEvent, ReactNode } from 'react';

import { InputField, TextareaField } from '@/design-system';

export type SkillFieldValues = {
  name?: string;
  description: string;
  body: string;
};

export type SkillFormFieldsProps = {
  values: SkillFieldValues;
  onChange(next: SkillFieldValues): void;
  disabled?: boolean;
  showName?: boolean;
  descriptionClassName?: string;
  bodyClassName?: string;
  bodyRows?: number;
  bodyLabel?: string;
  bodyHint?: ReactNode;
};

const LABEL = 'text-sm font-medium text-zinc-700 dark:text-zinc-300';
const HINT = 'text-xs text-muted';

export function SkillFormFields({
  values,
  onChange,
  disabled = false,
  showName = false,
  descriptionClassName,
  bodyClassName,
  bodyRows = 8,
  bodyHint,
  bodyLabel = 'Instructions',
}: SkillFormFieldsProps) {
  const set = <K extends keyof SkillFieldValues>(
    key: K,
    value: SkillFieldValues[K],
  ) => onChange({ ...values, [key]: value });

  return (
    <>
      {showName ? (
        <div className="flex flex-col gap-1.5">
          <label htmlFor="skill-fields-name" className={LABEL}>
            Display name{' '}
            <span className="font-normal text-zinc-400 dark:text-zinc-500">
              (optional)
            </span>
          </label>
          <InputField
            id="skill-fields-name"
            type="text"
            value={values.name ?? ''}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              set('name', e.target.value)
            }
            placeholder="Human-readable label"
            disabled={disabled}
          />
        </div>
      ) : null}

      <div className="flex flex-col gap-1.5">
        <label htmlFor="skill-fields-description" className={LABEL}>
          Description
        </label>
        <TextareaField
          id="skill-fields-description"
          rows={2}
          value={values.description}
          onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
            set('description', e.target.value.replace(/[\r\n]+/g, ' '))
          }
          placeholder="One-line summary of when to use this skill."
          disabled={disabled}
          className={`resize-none${descriptionClassName ? ` ${descriptionClassName}` : ''}`}
        />
        <p className={HINT}>
          The AI reads the description to{' '}
          <strong className="font-medium">search and find</strong> this skill — keep it a
          crisp, specific trigger phrase.
        </p>
      </div>

      <div className="flex flex-col gap-1.5">
        <label htmlFor="skill-fields-body" className={LABEL}>
          {bodyLabel}
        </label>
        <TextareaField
          id="skill-fields-body"
          rows={bodyRows}
          value={values.body}
          onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
            set('body', e.target.value)
          }
          className={`font-mono${bodyClassName ? ` ${bodyClassName}` : ''}`}
          disabled={disabled}
        />
        <p className={HINT}>
          Markdown — read by the AI{' '}
          <strong className="font-medium">once the skill is selected</strong>. Tell it
          when to use the skill and the steps to follow.
        </p>
        {bodyHint ? <div>{bodyHint}</div> : null}
      </div>
    </>
  );
}
